import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { usePage } from '@inertiajs/react'
import { cx, getAltPath } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'
import { useMarkdownCopy, useAIChat } from '@/hooks/useMarkdownCopy'

interface CopyPageDropdownProps {
  /** Optional className for the container */
  className?: string
}

export default function CopyPageDropdown({ className }: CopyPageDropdownProps) {
  const { url } = usePage()
  const { t } = useTranslations()
  const { copied, handleCopyMarkdown } = useMarkdownCopy()
  const { openAIChat } = useAIChat()
  const mdPath = getAltPath(url, '.md')
  const pdfPath = getAltPath(url, '.pdf')


  return (
    <div className={cx('relative flex', className)}>
      {/* Main Copy Button */}
      <button
        onClick={() => handleCopyMarkdown(url)}
        className={cx(
          'relative rounded-l-full text-center font-medium z-0 flex items-center justify-center',
          'text-sm px-3 py-1.5 w-32', // Match Contact support button height but fixed width
          'bg-primaryBlue text-white dark:text-secondary-800',
          'hover:bg-primary-700 hover:dark:bg-primary-100',
          'transition-colors duration-200',
          'focus:outline-none', // Remove focus ring entirely
          'border border-primaryBlue border-r-0', // Remove right border to connect with dropdown
          '!transform-none !scale-100', // Force prevent any scaling
          copied && 'bg-green-600 border-green-600 hover:bg-green-700 active:bg-green-700'
        )}
        style={{ transform: 'none', scale: '1' }} // Inline style to override any global scaling
      >
        <span className="font-bwi text-sm mr-2">&#xe905;</span>
        <span className="whitespace-nowrap">
          {copied ? t('Copied!') : t('Copy for LLM')}
        </span>
      </button>

      {/* Dropdown Arrow Button */}
      <Menu>
        <MenuButton
          className={cx(
            'relative rounded-r-full text-center font-medium z-0 flex items-center justify-center',
            'text-sm px-2 py-1.5', // Match height with smaller padding for arrow
            'bg-primaryBlue text-white dark:text-secondary-800',
            'hover:bg-primary-700 hover:dark:bg-primary-100',
            'transition-colors duration-200',
            'focus:outline-none', // Remove focus ring entirely
            'border border-primaryBlue border-l-primary-700',
            '!transform-none !scale-100' // Force prevent any scaling
          )}
          style={{ transform: 'none', scale: '1' }} // Inline style to override any global scaling
        >
          <span className="font-bwi text-sm">&#xe92d;</span> {/* Chevron down */}
        </MenuButton>

        <Transition
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <MenuItems
            anchor="bottom end"
            className={cx(
              'mt-2 w-80 rounded-lg bg-white dark:bg-secondary-900',
              'border border-secondary-200 dark:border-secondary-700 shadow-lg',
              'focus:outline-none z-20'
            )}
          >
            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => handleCopyMarkdown(url)}
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe905; {/* Copy icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Copy for LLM')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('Copy page as Markdown for LLMs')}
                      </span>
                    </div>
                  </button>
                )}
              </MenuItem>
            </div>

            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => openAIChat(url)}
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe990; {/* Chat icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Open AI Chat')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('Ask questions about this page')}
                      </span>
                    </div>
                  </button>
                )}
              </MenuItem>

              <MenuItem>
                {({ focus }) => (
                  <a
                    href={mdPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe94F; {/* File text icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Open page as Markdown')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('View markdown in new tab')}
                      </span>
                    </div>
                  </a>
                )}
              </MenuItem>
            </div>

            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <a
                    href={pdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe94e; {/* PDF download icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Download as PDF')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('Save page as PDF document')}
                      </span>
                    </div>
                  </a>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  )
}
